import React, { useState, useEffect } from "react";
import moment from 'moment-timezone';
import CountUp from "react-countup";
import {
  ArrowDownIcon,
  ArrowNarrowRightIcon,
  ArrowUpIcon,
  CalendarIcon, CashIcon,
  ChartBarIcon,
  ChatAltIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentTextIcon,
  DotsHorizontalIcon,
  EyeIcon,
  GlobeIcon,
  PencilAltIcon,
  PresentationChartBarIcon,
  SaveIcon,
  ShareIcon,
  StarIcon,
  TrashIcon,
  UserAddIcon
} from '@heroicons/react/solid';
import { Col, Row, Card, Form, Badge, Image, Button, ListGroup, ProgressBar, Tooltip, Dropdown, OverlayTrigger, ButtonGroup } from '@themesberg/react-bootstrap';
import { BarChartHorizontal, BarChart, PieChart, DognutChart, LineGraphChart, SalesValueChart, CustomersChart, WeeklyReportChart } from "./Charts";
import { Link, useHistory } from 'react-router-dom';

import Profile2 from "../assets/img/profile.png";
import ProfileCover from "../assets/img/profile.png";

import {FormattedMessage} from 'react-intl';
import {
  faAngleDoubleLeft, faAngleDoubleRight, faCar, faCarSide, faCaretDown, faCaretUp,
  faCreditCard, faFilter, faMoneyBill,
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

// ──────────────────────────────────────────────────────────────────
// Composants V2 du tableau de bord (DeltaChip / KpiTile / ConversionFunnel /
// YearSelector). Restylent l'écran statistiques ; tous scopés sous .ccv2-dashboard.
// ──────────────────────────────────────────────────────────────────

// Puce d'évolution partagée (héro + KPIs). Masquée si valeur nulle/0.
const DeltaChip = ({value, sinceId, decimals = 0, hideOnZero = true}) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (n === 0 && hideOnZero) return null;
  const flat = n === 0;
  const cls = flat ? 'is-flat' : (n > 0 ? 'is-up' : 'is-down');
  return (
    <span className={`ccv2-delta ${cls}`}>
      {!flat && <FontAwesomeIcon icon={n > 0 ? faCaretUp : faCaretDown}/>}
      {n.toFixed(decimals)}%
      <span className="ccv2-delta-since"><FormattedMessage id={sinceId}/></span>
    </span>
  );
};

// Tuile KPI du parcours client : icône + libellé + position + total (ou NO_DATA) +
// évolution + sparkline. metric = objet brut {total, evolution, data, labels}.
export const KpiTile = (props) => {
  const {step, icon, label, metric, isYearly, numberFormatter} = props;
  const hasTotal = !!(metric && metric.total);
  return (
    <div className="ccv2-card ccv2-kpi">
      <div className="ccv2-kpi-head">
        <span className="ccv2-card-icon"><FontAwesomeIcon icon={icon}/></span>
        <span className="ccv2-kpi-label">{label}</span>
        <span className="ccv2-kpi-step">{step}</span>
      </div>
      <div className="ccv2-kpi-value">
        {hasTotal
          ? numberFormatter.format(metric.total)
          : <span className="ccv2-na"><FormattedMessage id="NO_DATA"/></span>}
      </div>
      <DeltaChip value={metric?.evolution} sinceId={isYearly ? 'SINCE_LAST_YEAR' : 'SINCE_LAST_MONTH'} decimals={0}/>
      <div className="ccv2-kpi-spark">
        <CustomersChart data={metric} name={label} height={72}/>
      </div>
    </div>
  );
};

// Entonnoir de conversion proportionnel (Visiteurs → Estimations → Accès → Réservations).
// steps = [{key,label}], totals = [n,n,n,n], rate(a,b) garde la division par zéro.
export const ConversionFunnel = (props) => {
  const {steps, totals, rate, overall, numberFormatter, intl} = props;
  const base = totals[0];
  const hasBase = Number.isFinite(base) && base > 0;
  const ramp = [1, 0.82, 0.64, 0.46];

  return (
    <section className="ccv2-card ccv2-funnel-card">
      <div className="ccv2-card-head ccv2-funnel-head">
        <span className="ccv2-card-icon"><FontAwesomeIcon icon={faFilter}/></span>
        <span className="ccv2-card-title"><FormattedMessage id="CONVERSION_RATE"/></span>
        <span className="ccv2-funnel-overall">
          {overall == null ? '—' : overall + '%'} <FormattedMessage id="OVERALL_CONVERSION"/>
        </span>
      </div>
      {hasBase ? (
        <div className="ccv2-funnel-body">
          {steps.map((s, i) => {
            const t = totals[i];
            const frac = (Number.isFinite(t) && base > 0) ? Math.min(1, t / base) : 0;
            const conv = i > 0 ? rate(t, totals[i - 1]) : null;
            return (
              <React.Fragment key={s.key}>
                {i > 0 &&
                  <div className="ccv2-funnel-conv">
                    <span className="ccv2-funnel-arrow">&rarr;</span>
                    <span className="ccv2-funnel-rate">{conv == null ? '—' : conv + '%'}</span>
                    <span className="ccv2-funnel-caption"><FormattedMessage id="CONVERSION_RATE"/></span>
                  </div>}
                <div className="ccv2-funnel-stage">
                  <span className="ccv2-funnel-value">
                    {Number.isFinite(t) ? numberFormatter.format(t) : intl.formatMessage({id: 'NO_DATA'})}
                  </span>
                  <div className="ccv2-funnel-bartrack">
                    <div className="ccv2-funnel-bar"
                         style={{'--frac': frac, opacity: ramp[i], background: i === steps.length - 1 ? 'var(--cc-success)' : 'var(--cc-accent)'}}/>
                  </div>
                  <span className="ccv2-funnel-label"><FormattedMessage id={s.label}/></span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div className="ccv2-funnel-empty"><FormattedMessage id="NO_DATA"/></div>
      )}
    </section>
  );
};

// Sélecteur d'année V2 (remplace la Pagination Bootstrap). Conserve la fenêtre ±3 +
// les « … » au-delà de 5 années, et l'entrée TOTAL (année 0) épinglée en fin.
export const YearSelector = (props) => {
  const {years, activeYear, onPrev, onNext, onPick, intl} = props;
  const activeIndex = years.indexOf(activeYear);

  const pills = [];
  let previousDots = false;
  let nextDots = false;
  for (let i = 0; i < years.length; i++) {
    const year = years[i];
    const isActive = i === activeIndex;
    const cls = `ccv2-year-pill ${isActive ? 'is-active' : ''} ${year === 0 ? 'ccv2-year-pill--total' : ''}`;
    const pill = (
      <button key={year} type="button" className={cls} onClick={() => onPick(year)}>
        {year === 0 ? intl.formatMessage({id: 'TOTAL'}) : year}
      </button>
    );

    if (years.length <= 5) {
      pills.push(pill);
    } else if (isActive || year === 0 || (i >= activeIndex - 3 && i <= activeIndex + 3)) {
      // year === 0 : la pseudo-année TOTAL reste toujours épinglée (jamais avalée par les « … »).
      pills.push(pill);
    } else if (!previousDots && i < activeIndex) {
      previousDots = true;
      pills.push(<button key="previousDots" type="button" className="ccv2-year-pill is-dots" disabled>…</button>);
    } else if (!nextDots && i > activeIndex) {
      nextDots = true;
      pills.push(<button key="nextDots" type="button" className="ccv2-year-pill is-dots" disabled>…</button>);
    }
  }

  return (
    <div className="ccv2-year-selector">
      <button type="button" className="ccv2-year-nav" onClick={onPrev} aria-label="prev">
        <FontAwesomeIcon icon={faAngleDoubleLeft}/>
      </button>
      <div className="ccv2-year-track">{pills}</div>
      <button type="button" className="ccv2-year-nav" onClick={onNext} aria-label="next">
        <FontAwesomeIcon icon={faAngleDoubleRight}/>
      </button>
    </div>
  );
};

const PeriodOverviewWidget = (props) => {
  const { category, data, title, period, percentage, isYearly, ChartComponent = CustomersChart } = props;
  const PercentageIcon = percentage < 0 ? ChevronDownIcon : ChevronUpIcon;
  const percentageColor = percentage < 0 ? "text-danger" : "text-success";

  return (
    <Card border="0" className="shadow">
      <Card.Body>
        <Row className="d-block d-xxl-flex align-items-center">
          <Col xs={12} xxl={6} className="px-xxl-0 mb-3 mb-xxl-0">
            <ChartComponent data={data} name={category} />
          </Col>
          <Col xs={12} xxl={6} className="ps-xxl-4 pe-xxl-0">
            <h2 className="fs-5 fw-normal mb-1">
              {category}
            </h2>
            <h3 className="fw-extrabold mb-1">
              {title}
            </h3>
            {period && <small className="d-flex align-items-center">
              <CalendarIcon className="icon icon-xxs text-gray-400 me-1" />
              {period}
            </small>}
            {percentage !== undefined && percentage !== 0 && <div className="small d-flex mt-1">
              <PercentageIcon className={`icon icon-sm ${percentageColor}`} />
              <span className={`${percentageColor} fw-bolder me-1`}>
                {Number(percentage).toFixed(0)}%
              </span> {isYearly && <FormattedMessage id="SINCE_LAST_YEAR"/>}{!isYearly && <FormattedMessage id="SINCE_LAST_MONTH"/>}
            </div>}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export const CardWidget = (props) => {
  const { title, value, period, country } = props;

  return (
    <Card border="0" className="shadow">
      <Card.Body>
        <h2 className="fs-5 fw-normal">
          {title}
        </h2>
        <h3 className="fs-1 fw-extrabold mb-1">
          {value}
        </h3>
        <div className="d-flex align-items-center">
          <span className="me-3">
            {period}
          </span>
          <GlobeIcon className="icon icon-xxs text-gray-500 me-1" />
          <span>{country}</span>
        </div>
      </Card.Body>
    </Card>
  );
};

export const ProfileCardWidget = (props) => {
  const { fullName, picture, jobTitle, location } = props;

  return (
    <Card border="0" className="shadow text-center p-0">
      <div style={{ backgroundImage: `url(${ProfileCover})` }} className="profile-cover rounded-top" />
      <Card.Body className="pb-5">
        <Card.Img src={picture} className="avatar-xl rounded-circle mx-auto mt-n7 mb-4" />
        <Card.Title>{fullName}</Card.Title>
        <Card.Subtitle className="fw-normal">
          {jobTitle}
        </Card.Subtitle>
        <Card.Text className="text-gray mb-4">
          {location}
        </Card.Text>

        <Button variant="gray-800" size="sm" className="d-inline-flex align-items-center me-2">
          <UserAddIcon className="icon icon-sm me-1" /> Connect
        </Button>
        <Button variant="secondary" size="sm">Send Message</Button>
      </Card.Body>
    </Card>
  );
};

export const PriceCardWidget = (props) => {
  const { variant = "gray-800", priceType = "monthly", priceMonthly, priceYearly, period = "month", title, description, features = [], btnText, btnTextColor = "" } = props;
  const btnClassName = btnTextColor ? `w-100 text-${btnTextColor}` : `w-100`;
  const price = priceType === "monthly" ? priceMonthly : priceYearly;

  const Feature = ({ text }) => {
    return (
      <div className="d-flex align-items-center mb-3">
        <CheckIcon className="icon icon-sm me-2" />
        <span>{text}</span>
      </div>
    );
  };

  return (
    <Card className="mb-4 mb-xl-0">
      <Card.Header className="border-gray-100 py-5 px-4">
        <div className="d-flex mb-3">
          <h5 className="mb-0">$</h5>
          <span className={`price display-2 text-${variant} mb-0`}>
            <CountUp start={priceMonthly} end={price} duration={2} />
          </span>
          <h6 className="fw-normal align-self-end">/{period}</h6>
        </div>
        <h4 className="mb-3 text-black">{title}</h4>
        <p className="fw-normal mb-0">{description}</p>
      </Card.Header>
      <Card.Body className="py-5 px-4">
        {features.map((text, index) => <Feature key={`feature-${index}`} text={text} />)}
      </Card.Body>
      <Card.Footer className="border-gray-100 d-grid px-4 pb-4">
        <Button as={Link} variant={variant} target="_blank" className={btnClassName}>
          {btnText} <ArrowNarrowRightIcon className="icon icon-sm ms-3" />
        </Button>
      </Card.Footer>
    </Card>
  );
};

export const OrderHistoryWidget = () => {
  const InvoiceItem = (props) => {
    const { invoiceNumber, invoiceDate, invoiceStatus, last } = props
      , invoiceDateFormatted = invoiceDate.format("MMMM DD, YYYY")
      , isInvoicePaid = invoiceStatus === "paid"
      , listItemClassNames = last ? "py-3" : "border-bottom py-3";

    return (
      <ListGroup.Item className={listItemClassNames}>
        <Row className="align-items-center">
          <Col>
            <h6 className="mb-1">
              <a href="#!">Invoice #{invoiceNumber}</a>
            </h6>
            <small className="text-gray-700">
              Billed {invoiceDateFormatted}
            </small>
          </Col>
          <Col xs="auto">
            {isInvoicePaid ? (
              <Badge bg="success" className="rounded px-3 py-2">
                <span className="text-uppercase fw-bolder">
                  Paid
                </span>
              </Badge>
            ) : (
              <Button variant="gray-800" size="sm">
                Pay now
              </Button>
            )}
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  return (
    <Card border="0" className="shadow p-0 p-md-4">
      <Card.Header className="border-bottom p-3">
        <h5>Order History</h5>
        <Card.Text className="mb-0">
          Manage billing information and view receipts
        </Card.Text>
      </Card.Header>
      <Card.Body className="px-0 py-0">
        <ListGroup>
          <InvoiceItem
            invoiceNumber="120345"
            invoiceStatus="issued"
            invoiceDate={moment().subtract("1", "day")}
          />
          <InvoiceItem
            last={true}
            invoiceNumber="120344"
            invoiceStatus="paid"
            invoiceDate={moment().subtract("2", "days")}
          />
        </ListGroup>
      </Card.Body>
    </Card>
  );
};

export const ListChartWidget = () => {
  return (
    <Card border="0" className="shadow">
      <Card.Body>
        <h5>Related Sites</h5>
        <ul className="list-unstyled mb-0">
          <li className="mb-1">
            {/*<GoogleIcon size="xxs" color="gray-500" className="me-2" />*/}
            google.com
          </li>
          <li className="mb-1">
            {/*<YoutubeIcon size="xxs" color="gray-500" className="me-2" />*/}
            youtube.com
          </li>
          <li className="mb-1">
            {/*<TwitterIcon size="xxs" color="gray-500" className="me-2" />*/}
            twitter.com
          </li>
        </ul>
      </Card.Body>
    </Card>
  );
};

export const CustomersWidget = (props) => {
  return (
    <PeriodOverviewWidget
      {...props}
      ChartComponent={CustomersChart}
    />
  );
};

export const WeeklyReportWidget = (props) => {
  const { headerTitle, headerSubtitle, reportTitle, reportSubtitle } = props;

  return (
    <Card border="0" className="shadow">
      <Card.Header className="border-bottom">
        <h2 className="fs-5 fw-bold mb-1">
          {headerTitle}
        </h2>
        <small>{headerSubtitle}</small>
      </Card.Header>
      <Card.Body className="text-center py-4 py-xl-5">
        <h3 className="display-3 fw-extrabold mb-0">
          {reportTitle}
        </h3>
        <p>{reportSubtitle}</p>
        <Button variant="primary" className="d-inline-flex align-items-center">
          <DocumentTextIcon className="icon icon-xxs me-2" />
          Generate Report
        </Button>
      </Card.Body>
      <Card.Footer className="border-0 px-3 py-4">
        <WeeklyReportChart />
      </Card.Footer>
    </Card>
  );
};

export const BarChartHorizontalWidget = (props) => {
  const { title, data = [] } = props;
  const dataOrderedAsc = [...data].sort((a, b) => a.value - b.value);
  const dataOrderedDesc = [...data].sort((a, b) => b.value - a.value);

  const topLabel = dataOrderedDesc[0] ? dataOrderedDesc[0].label : "No data";
  const topValue = dataOrderedDesc[0] ? dataOrderedDesc[0].value : 0;

  const PercentageIcon = topValue < 0 ? ChevronDownIcon : ChevronUpIcon;
  const percentageColor = topValue < 0 ? "text-danger" : "text-success";

  return (
    <Card border="0" className="shadow">
      <Card.Body className="d-flex flex-row align-items-center flex-0 border-bottom">
        <div className="d-block">
          <h6 className="fw-normal text-gray mb-2">
            {title}
          </h6>
          <h4>{topLabel}</h4>
          <small className={`d-flex align-items-center ${percentageColor} mt-2`}>
            <PercentageIcon className="icon icon-sm" />
            <span className="fw-bold">{topValue}%</span>
          </small>
        </div>
        <div className="ms-auto">
          {dataOrderedDesc.map(d => (
            <div key={`bar-chart-${d.id}`} className="d-flex align-items-center mb-2">
              <small className="fw-normal me-3">{d.label}</small>
              <small className="fw-bold text-dark ms-auto">{d.value}%</small>
            </div>
          ))}
        </div>
      </Card.Body>
      <Card.Body className="p-2 py-5">
        <BarChartHorizontal
          title={title}
          data={dataOrderedAsc}
        />
      </Card.Body>
    </Card>
  );
};

export const BarChartWidget = (props) => {
  const { title, worldRank, data = [] } = props;

  return (
    <Card border="0" className="shadow">
      <Card.Body className="d-flex flex-row align-items-center flex-0 border-bottom">
        <div className="d-block">
          <div className="mb-3">
            <h2 className="fs-3 fw-extrabold">
              {title}
            </h2>
            <div className="small mt-2 d-flex">
              <div className="d-flex align-items-center me-2">
                <ChevronUpIcon className="icon icon-sm text-success" />
                <span className="text-success fw-bold">
                  {worldRank}
                </span>
              </div>
              <div className="d-flex align-items-center">
                <GlobeIcon className="icon icon-xxs text-gray-500 me-1" />
                <span>WorldWide</span>
              </div>
            </div>
          </div>
          <div className="d-flex">
            {data.map(d => (
              <div key={`bar-chart-${d.id}`} className="d-flex align-items-center me-3 lh-130">
                <span style={{ backgroundColor: d.color }} className="dot rounded-circle me-2" />
                <small className="fw-normal">{d.label}</small>
              </div>
            ))}
          </div>
        </div>
      </Card.Body>
      <Card.Body className="p-2">
        <BarChart data={data} />
      </Card.Body>
    </Card>
  );
};

export const PieChartWidget = (props) => {
  const { title, data = [] } = props;
  const series = data.reduce((acc, curr) => {
    acc.values.push(curr.value);

    if (acc.top.value < curr.value) {
      acc.top = curr;
    }

    return acc
  }, { values: [], top: { value: 0, label: "No data" } });

  const PercentageIcon = series.top.value < 0 ? ArrowDownIcon : ArrowUpIcon;
  const percentageColor = series.top.value < 0 ? "text-danger" : "text-success";

  return (
    <Card border="0" className="shadow">
      <Card.Body className="d-flex flex-row align-items-center flex-0 border-bottom">
        <div className="d-block">
          <h6 className="fw-normal text-gray mb-2">{title}</h6>
          <h4>{series.top.label}</h4>
          <small className={`${percentageColor} mt-2`}>
            <PercentageIcon className="icon icon-sm me-1" />
            <span className="fw-bold">{series.top.value}%</span>
          </small>
        </div>
        <div className="d-block ms-auto">
          {data.map(d => (
            <div key={`pie-element-${d.id}`} className="d-flex align-items-center text-end mb-2">
              <span className={`shape-xs rounded-circle bg-${d.color} me-2`} />
              <span className="fw-normal small">{d.label}</span>
            </div>
          ))}
        </div>
      </Card.Body>
      <Card.Body className="p-2">
        <PieChart series={series.values} />
      </Card.Body>
    </Card>
  );
};

export const DognutChartWidget = (props) => {
  const { title, value, percentage, data = [] } = props;
  const series = data.map(d => d.value);
  const PercentageIcon = percentage < 0 ? ArrowDownIcon : ArrowUpIcon;
  const percentageColor = percentage < 0 ? "text-danger" : "text-success";

  return (
    <Card border="0" className="shadow">
      <Card.Body className="d-flex flex-row align-items-center flex-0 border-bottom">
        <div className="d-block">
          <h6 className="fw-normal text-gray mb-2">{title}</h6>
          <h4>{value}</h4>
          <small className={`${percentageColor} mt-2`}>
            <PercentageIcon className="me-1" />
            <span className="fw-bold">
              {percentage}%
            </span>
          </small>
        </div>
        <div className="d-block ms-auto">
          {data.map(d => (
            <div key={`dognut-element-${d.id}`} className="d-flex align-items-center text-end mb-2">
              <span className={`shape-xs rounded-circle bg-${d.color} me-2`} />
              <small className="fw-normal">{d.label}</small>
            </div>
          ))}
        </div>
      </Card.Body>
      <Card.Body className="p-2">
        <DognutChart series={series} />
      </Card.Body>
    </Card>
  );
};

export const TeamMembersWidget = (teamMembers) => {
  const history = useHistory();

  const goToUsers = () => {
    // history.push(Routes.Users.path);
  }

  const TeamMember = (props) => {
    const { name, statusKey, image, icon: ButtonIcon = CalendarIcon, btnText } = props;
    const status = {
      online: { color: "success", label: "Online" },
      inMeeting: { color: "warning", label: "In a meeting" },
      offline: { color: "danger", label: "Offline" }
    };

    const statusColor = status[statusKey] ? status[statusKey].color : 'danger'
      , statusLabel = status[statusKey] ? status[statusKey].label : 'Offline';

    return (
      <ListGroup.Item className="px-0">
        <Row className="align-items-center">
          <Col xs="auto">
            <Card.Link href="#top" className="avatar">
              <Image src={image} className="rounded m-0" />
            </Card.Link>
          </Col>
          <Col xs="auto" className="ms--2">
            <h4 className="h6 mb-0">
              <Card.Link href={`#${name}`}>
                {name}
              </Card.Link>
            </h4>
            <div className="d-flex align-items-center">
              <div className={`bg-${statusColor} dot rounded-circle me-1`} />
              <small>{statusLabel}</small>
            </div>
          </Col>
          <Col className="text-end">
            <Button variant="secondary" size="sm" className="d-inline-flex align-items-center">
              <ButtonIcon className="icon icon-xxs me-2" /> {btnText}
            </Button>
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  return (
    <Card border="0" className="shadow">
      <Card.Header className="border-bottom d-flex align-items-center justify-content-between">
        <h2 className="fs-5 fw-bold mb-0">
          Team members
        </h2>
        <Button variant="primary" size="sm" onClick={goToUsers}>
          See all
        </Button>
      </Card.Header>
      <Card.Body>
        <ListGroup className="list-group-flush list my--3">
          {teamMembers.map(tm => <TeamMember key={`team-member-${tm.id}`} {...tm} />)}
        </ListGroup>
      </Card.Body>
    </Card>
  );
};

export const PaymentsProgressTrackWidget = (props) => {
  const { intl, data } = props;
  const driverRaw = data?.driver;
  const onlineRaw = data?.online;
  const hasData = !!driverRaw || !!onlineRaw;
  const sum = (driverRaw || 0) + (onlineRaw || 0);
  const dW = sum > 0 ? (driverRaw || 0) / sum * 100 : 0;
  const oW = sum > 0 ? (onlineRaw || 0) / sum * 100 : 0;

  return (
    <section className="ccv2-card">
      <div className="ccv2-card-head">
        <span className="ccv2-card-icon"><FontAwesomeIcon icon={faCreditCard}/></span>
        <span className="ccv2-card-title"><FormattedMessage id="PAYMENTS"/></span>
      </div>
      {hasData ? (
        <>
          <div className="ccv2-split-bar">
            <div className="ccv2-split-seg ccv2-split-seg--driver" style={{width: `${dW}%`}}/>
            <div className="ccv2-split-seg ccv2-split-seg--online" style={{width: `${oW}%`}}/>
          </div>
          <div className="ccv2-split-legend">
            <span className="ccv2-split-item">
              <FontAwesomeIcon icon={faMoneyBill} className="ccv2-split-ico--driver"/>
              {intl.formatMessage({id: 'DRIVER_PAYMENT'})} <b>{(driverRaw || 0).toFixed(0)}%</b>
            </span>
            <span className="ccv2-split-item">
              <FontAwesomeIcon icon={faCreditCard} className="ccv2-split-ico--online"/>
              {intl.formatMessage({id: 'ONLINE_PAYMENT'})} <b>{(onlineRaw || 0).toFixed(0)}%</b>
            </span>
          </div>
        </>
      ) : (
        <div className="ccv2-split-empty"><FormattedMessage id="NO_DATA"/></div>
      )}
    </section>
  );
};

export const VehiclesProgressTrackWidget = (props) => {
  const { data = [] } = props;
  const rows = Array.isArray(data) ? data : [];

  return (
    <section className="ccv2-card">
      <div className="ccv2-card-head">
        <span className="ccv2-card-icon"><FontAwesomeIcon icon={faCarSide}/></span>
        <span className="ccv2-card-title"><FormattedMessage id="BOOKINGS_PER_VEHICLE"/></span>
      </div>
      {rows.length > 0 ? (
        rows.map((item, idx) => {
          const pct = (item?.percentage ?? 0).toFixed(0);
          return (
            <div className="ccv2-dist-row" key={`vehicle-dist-${idx}`}>
              <div className="ccv2-dist-top">
                <span className="ccv2-dist-label">{item?.name ?? '—'}</span>
                <span className="ccv2-dist-pct">{pct}%</span>
              </div>
              <div className="ccv2-dist-track">
                <div className="ccv2-dist-fill" style={{width: `${pct}%`}}/>
              </div>
            </div>
          );
        })
      ) : (
        <div className="ccv2-dash-empty">—</div>
      )}
    </section>
  );
};

export const RankingWidget = (props) => {
  const { data = [], formatter, lengthUnit } = props;

  const formatNumber = (n) => {
    if (n === null || n === undefined || Number.isNaN(n)) return '-';
    const num = typeof n === 'number' ? n : Number(n);
    return new Intl.NumberFormat(navigator.language, { maximumFractionDigits: 2 }).format(num);
  };
  // Garde contre les distances nulles (corrige le crash v?.distanceKm.toFixed sur null).
  const distance = (v) => {
    const km = v?.distanceKm ?? 0;
    return (lengthUnit === 'MI' ? km * 0.621371 : km).toFixed(2);
  };
  const unit = lengthUnit === 'MI' ? 'MI' : 'KM';
  const rows = Array.isArray(data) ? data : [];

  return (
    <section className="ccv2-card">
      <div className="ccv2-card-head">
        <span className="ccv2-card-icon"><FontAwesomeIcon icon={faCar}/></span>
        <span className="ccv2-card-title"><FormattedMessage id="VEHICLE_RANKING"/></span>
      </div>

      {rows.length > 0 ? (
        <>
          <div className="ccv2-dash-tablewrap">
            <table className="ccv2-dash-table">
              <thead>
                <tr>
                  <th className="ccv2-rank-index">#</th>
                  <th><FormattedMessage id="VEHICLES"/></th>
                  <th className="ccv2-num"><FormattedMessage id="BILLED"/></th>
                  <th className="ccv2-num"><FormattedMessage id="BOOKINGS"/></th>
                  <th className="ccv2-num">{unit} <FormattedMessage id="BILLED"/></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v, idx) => (
                  <tr key={`vehicle-row-${idx}`}>
                    <td className="ccv2-rank-index">{idx + 1}</td>
                    <td><span className="ccv2-veh"><FontAwesomeIcon icon={faCarSide}/>{v?.name || '—'}</span></td>
                    <td className="ccv2-num">{formatter.format(v?.sales)}</td>
                    <td className="ccv2-num">{formatNumber(v?.bookings)}</td>
                    <td className="ccv2-num">{distance(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ccv2-vrow-list">
            {rows.map((v, idx) => (
              <div className="ccv2-vrow" key={`vehicle-card-${idx}`}>
                <div className="ccv2-vrow-head"><FontAwesomeIcon icon={faCarSide}/>{v?.name || '—'}</div>
                <div className="ccv2-vrow-stat">
                  <span className="ccv2-vrow-k"><FormattedMessage id="BILLED"/></span>
                  <span className="ccv2-vrow-v">{formatter.format(v?.sales)}</span>
                </div>
                <div className="ccv2-vrow-stat">
                  <span className="ccv2-vrow-k"><FormattedMessage id="BOOKINGS"/></span>
                  <span className="ccv2-vrow-v">{formatNumber(v?.bookings)}</span>
                </div>
                <div className="ccv2-vrow-stat">
                  <span className="ccv2-vrow-k">{unit} <FormattedMessage id="BILLED"/></span>
                  <span className="ccv2-vrow-v">{distance(v)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="ccv2-dash-empty">—</div>
      )}
    </section>
  );
};

export const LineGraphChartWidget = (props) => {
  const { title, data = [] } = props;
  const dataWithTotal = data.map(d => ({ ...d, total: d.values.map(v => v.value).reduce((a, b) => a + b) }));
  dataWithTotal.sort((a, b) => a.total + b.total);

  const valuesTotal = dataWithTotal.reduce((a, b) => a + b.total, 0);
  const topLabel = dataWithTotal[0] ? dataWithTotal[0].label : "No data";

  const PercentageIcon = valuesTotal < 0 ? ChevronDownIcon : ChevronUpIcon;
  const percentageColor = valuesTotal < 0 ? "text-danger" : "text-success";

  const formatNumber = number => {
    return new Intl.NumberFormat("en-GB", {
      notation: "compact",
      compactDisplay: "short",
      minimumFractionDigits: 1,
    }).format(number);
  };

  return (
    <Card border="0" className="shadow">
      <Card.Body className="d-flex flex-row align-items-center justify-content-between flex-0 border-bottom">
        <div>
          <div className="fs-5 fw-normal mb-2">
            {title}
          </div>
          <h2 className="fs-3 fw-extrabold">
            {topLabel}
          </h2>
          <small className="d-flex mt-2">
            <div className={`d-flex align-items-center ${percentageColor} me-2`}>
              <PercentageIcon className="icon icon-sm" />
              <span className="fw-bold">
                {formatNumber(valuesTotal)}
              </span>
            </div>
            <div className="d-flex align-items-center">
              <GlobeIcon className="icon icon-xxs text-gray-500 me-1" />
              <span>WorldWide</span>
            </div>
          </small>
        </div>
        <div className="d-xxl-flex flex-wrap justify-content-end mt-4 mt-sm-0">
          {dataWithTotal.map(d => (
            <div key={`line-chart-${d.id}`} className="d-flex align-items-center mb-2 me-3 lh-130">
              <span style={{ backgroundColor: d.color }} className={`dot rounded-circle me-2`} />
              <small className="fw-normal">{d.label}</small>
              <small className="fw-bold text-dark ms-1">{formatNumber(d.total)}</small>
            </div>
          ))}
        </div>
      </Card.Body>
      <Card.Body className="p-2 pb-5">
        <LineGraphChart data={data} />
      </Card.Body>
    </Card>
  );
};

export const RatingsWidget = () => {
  const Review = (props) => {
    const { starsCount } = props;

    return (
      <Row className="d-flex align-items-center mb-2">
        <Col xs={2} color="gray" className="d-flex align-items-center fw-bold px-0 small">
          {starsCount}
          <StarIcon className="icon icon-sm text-warning ms-1" />
        </Col>
        <Col xs={10} className="px-0">
          <ProgressBar
            min={0}
            max={100}
            now={starsCount * 10}
            variant="primary"
            className="rounded progress-lg mb-0"
          />
        </Col>
      </Row>
    );
  };

  return (
    <Card border="0" className="shadow">
      <Card.Body>
        <Row className="d-block d-md-flex align-items-center">
          <Col xs={12} md={5}>
            <h2 className="fs-5 fw-normal mb-1">
              Average Rating
            </h2>
            <h3 className="fs-1 fw-extrabold mb-1">
              4.5
            </h3>
            <div className="mb-2">
              <StarIcon className="icon icon-sm text-warning" />
              <StarIcon className="icon icon-sm text-warning" />
              <StarIcon className="icon icon-sm text-warning" />
              <StarIcon className="icon icon-sm text-warning" />
              <StarIcon className="icon icon-sm text-warning" />
            </div>
            <small>Based on <span color="dark" className="fw-bold">103,456</span> ratings</small>
          </Col>
          <Col xs={12} md={7} className="mt-3 mt-md-0">
            <Col xs={12}>
              <Review starsCount={5} />
              <Review starsCount={4} />
              <Review starsCount={3} />
              <Review starsCount={2} />
              <Review starsCount={1} />
            </Col>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export const NotificationsWidget = () => {
  const SettingsItem = (props) => {
    const { id, title, subtitle, enabled = false, last = false } = props;
    const borderBottomClass = !last ? "border-bottom" : "";

    return (
      <ListGroup.Item className={`d-flex align-items-center justify-content-between px-0 ${borderBottomClass}`}>
        <div>
          <Card.Text className="h6 mb-1">{title}</Card.Text>
          <p className="small pe-4">{subtitle}</p>
        </div>
        <div>
          <Form.Check className="form-switch">
            <Form.Check.Input defaultChecked={enabled} type="checkbox" id={`user-notification-${id}`} />
          </Form.Check>
        </div>
      </ListGroup.Item>
    );
  };

  return (
    <Card border="0" className="shadow mb-4 mb-xl-0">
      <Card.Body>
        <h5 className="mb-4">Alerts &amp; Notifications</h5>

        <ListGroup className="list-group-flush">
          <SettingsItem
            id={1}
            enabled={false}
            title="Company News"
            subtitle="Get Rocket news, announcements, and product updates"
          />
          <SettingsItem
            id={2}
            enabled={true}
            title="Account Activity"
            subtitle="Get important notifications about you or activity you've missed"
          />
          <SettingsItem
            id={3}
            last={true}
            enabled={true}
            title="Meetups Near You"
            subtitle="Get an email when a Dribbble Meetup is posted close to my location"
          />
        </ListGroup>
      </Card.Body>
    </Card>
  );
};

export const SalesValueWidget = (props) => {
  const { title, formatter, percentage, data, isCurrentYear } = props;
  const [period, setPeriod] = useState("year");

  // Auto-switch to 'year' when a non-current year is selected via pagination
  useEffect(() => {
    if (!isCurrentYear && period !== 'year') {
      setPeriod('year');
    }
  }, [isCurrentYear, period]);

  // Garde contre les séries manquantes (corrige le reduce non protégé d'origine).
  const total = (data?.data?.[period] || []).reduce((sum, value) => sum + value, 0);

  return (
    <section className="ccv2-card ccv2-hero">
      <div className="ccv2-hero-head">
        <div className="ccv2-hero-meta">
          <span className="ccv2-hero-label">{title}</span>
          <div className="ccv2-hero-value">{formatter.format(total)}</div>
          {isCurrentYear && <DeltaChip value={percentage} sinceId="SINCE_LAST_MONTH" decimals={2} hideOnZero={false}/>}
        </div>
        <div className="ccv2-seg" role="tablist">
          {isCurrentYear && ['day', 'week', 'month'].map(p => (
            <button key={p} type="button" role="tab"
                    className={`ccv2-seg-tab ${period === p ? 'is-active' : ''}`}
                    onClick={() => setPeriod(p)}>
              <FormattedMessage id={p.toUpperCase()}/>
            </button>
          ))}
          <button type="button" role="tab"
                  className={`ccv2-seg-tab ${period === 'year' ? 'is-active' : ''}`}
                  onClick={() => setPeriod('year')}>
            <FormattedMessage id="YEAR"/>
          </button>
        </div>
      </div>
      <div className="ccv2-hero-chart">
        <SalesValueChart period={period} data={data} name={title}/>
      </div>
    </section>
  );
};

export const AcquisitionWidget = () => {
  return (
    <Card border="0" className="shadow">
      <Card.Body>
        <h2 className="fs-5 fw-bold mb-1">
          Acquisition
        </h2>
        <p>Tells you where your visitors originated from, such as search engines, social networks or website referrals.</p>
        <div className="d-block">
          <div className="d-flex align-items-center me-5">
            <div className="icon-shape icon-sm icon-shape-danger rounded me-3">
              <PresentationChartBarIcon />
            </div>
            <div className="d-block">
              <label className="mb-0">Bounce Rate</label>
              <h4 className="mb-0">33.50%</h4>
            </div>
          </div>
          <div className="d-flex align-items-center pt-3">
            <div className="icon-shape icon-sm icon-shape-purple rounded me-3">
              <ChartBarIcon />
            </div>
            <div className="d-block">
              <label className="mb-0">Sessions</label>
              <h4 className="mb-0">9,567</h4>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export const TimelineWidget = (props) => {
  const { productNotifications, title } = props;

  const TimelineItem = (props) => {
    const { title, message, time, icon: ItemIcon, iconBg } = props;
    const receiveTime = moment(time).fromNow();

    return (
      <ListGroup.Item className="border-o">
        <Row className="ps-lg-1">
          <Col xs="auto">
            <div className={`icon-shape icon-sm icon-shape-${iconBg} rounded`}>
              <ItemIcon />
            </div>
          </Col>
          <Col className="ms-n2 mb-3">
            <h3 className="fs-6 fw-bold mb-1">
              {title}
            </h3>
            <Card.Text className="mb-1">
              {message}
            </Card.Text>
            <div className="d-flex align-items-center">
              <ClockIcon className="icon icon-xxs text-gray-400 me-1" />
              <small>{receiveTime}</small>
            </div>
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  return (
    <Card border="0" className="notification-card shadow">
      <Card.Header className="d-flex align-items-center">
        <h2 className="fs-5 fw-bold mb-0">
          {title}
        </h2>
        <div className="ms-auto">
          <Card.Link href="#" className="d-inline-flex align-items-center fw-normal">
            <EyeIcon className="icon icon-xxs me-2" />
            View all
          </Card.Link>
        </div>
      </Card.Header>
      <Card.Body>
        <ListGroup className="list-group-flush list-group-timeline">
          {productNotifications.map(notif => <TimelineItem key={`notif-${notif.id}`} {...notif} />)}
        </ListGroup>
      </Card.Body>
    </Card>
  );
};

export const BlogCardWidget = () => {
  return (
    <Card border="0" className="shadow p-2 p-md-4">
      <Card.Header className="rounded rounded-0 pt-0">
        <div className="post-meta">
          <div className="media d-flex align-items-center justify-content-between">
            <div className="post-group">
              <OverlayTrigger placement="top" overlay={<Tooltip className="m-0">23k followers</Tooltip>}>
                <div><Image src={Profile2} className="avatar-sm me-2 m-0 img-fluid rounded-circle" alt="avatar" /> techhub</div>
              </OverlayTrigger>
            </div>
            <div className="d-flex align-items-center">
              <div className="author-name text-dark font-weight-bold font-small me-4 d-none d-lg-block">
                Posted by <Card.Link href="#top" className="me-2">@bonniegreen</Card.Link>5 hours ago
              </div>
              <Dropdown>
                <Dropdown.Toggle split variant="link" className="m-0 p-0">
                  <DotsHorizontalIcon className="icon icon-sm icon-dark" />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item href="#">
                    <PencilAltIcon className="dropdown-icon text-gray-400 me-2" /> Edit post
                  </Dropdown.Item>
                  <Dropdown.Item href="#">
                    <TrashIcon className="dropdown-icon text-danger me-2" /> Delete post
                  </Dropdown.Item >
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="py-4 px-0">
        <Card.Link href="#profile">
          <Image src={ProfileCover} className="card-img-top rounded m-0" alt="blog image" />
          <h4 className="my-4">List of public corporations by market capitalization</h4>
        </Card.Link>
        <Card.Text className="mb-4">
          All of the world's 10 largest companies as measured by market capitalization are American. Market capitalization is the total value of a company's entire purchased shares of stock. While these companies...
        </Card.Text>
      </Card.Body>
      <Card.Footer className="bg-white border-0 pt-0 px-0">
        <div className="d-flex flex-wrap flex-lg-nowrap align-items-center justify-content-between">
          <div className="post-details mb-3 mb-lg-0">
            <Button size="sm" variant="primary" className="animate-hover me-2">
              <ArrowUpIcon className="me-2 animate-up-2" /> 4
            </Button>
            <Button size="sm" variant="danger" className="animate-hover">
              <ArrowDownIcon className="me-2 animate-up-2" /> 1
            </Button>
          </div>
          <div className="post-meta">
            <Card.Link className="text-dark me-3" href="#comments">
              <ChatAltIcon className="icon icon-sm me-2" /> 33.7k
            </Card.Link>
            <Button variant="link" className="me-3 text-primary">
              <ShareIcon className="icon icon-sm me-2" /> Share
            </Button>
            <Button variant="secondary">
              <SaveIcon className="icon icon-sm me-2" /> Save
            </Button>
          </div>
        </div>
      </Card.Footer>
    </Card>
  );
};